import { useState, useCallback } from 'react';
import {
  AnalyzeCsvResponse,
  CsvMapping,
  ImportResponse,
} from '@tinct/core-api-models';

// Unlike the audience flow, buyers are imported straight into the
// organization — there is no "create + name" step, so the machine starts at
// `add`.
export type ModalStep = 'add' | 'mapping' | 'importing' | 'success';

export type BuyerSourceType = 'csv' | 'companies' | 'crm';

export type UseImportBuyerModalReturn = {
  // Step management
  step: ModalStep;
  setStep: (step: ModalStep) => void;

  // CSV state
  fileBase64: string | null;
  setFileBase64: (base64: string | null) => void;
  analysisResult: AnalyzeCsvResponse | null;
  setAnalysisResult: (result: AnalyzeCsvResponse | null) => void;
  mapping: CsvMapping;
  setMapping: (mapping: CsvMapping) => void;

  // Import result
  importResult: ImportResponse | null;
  setImportResult: (result: ImportResponse | null) => void;

  // Navigation helpers
  goToMapping: (analysisResult: AnalyzeCsvResponse, base64: string) => void;
  goToImporting: () => void;
  goToSuccess: (result: ImportResponse) => void;
  goBack: () => void;
  reset: () => void;
};

export const useImportBuyerModal = (): UseImportBuyerModalReturn => {
  const [step, setStep] = useState<ModalStep>('add');

  // CSV state
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] =
    useState<AnalyzeCsvResponse | null>(null);
  const [mapping, setMapping] = useState<CsvMapping>({});

  // Import result
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);

  const goToMapping = useCallback(
    (result: AnalyzeCsvResponse, base64: string) => {
      setAnalysisResult(result);
      setFileBase64(base64);
      if (result.autoMapping) {
        setMapping(result.autoMapping);
      }
      setStep('mapping');
    },
    []
  );

  const goToImporting = useCallback(() => {
    setStep('importing');
  }, []);

  const goToSuccess = useCallback((result: ImportResponse) => {
    setImportResult(result);
    setStep('success');
  }, []);

  const goBack = useCallback(() => {
    if (step === 'mapping') {
      setStep('add');
      setAnalysisResult(null);
      setFileBase64(null);
      setMapping({});
    }
  }, [step]);

  const reset = useCallback(() => {
    setStep('add');
    setFileBase64(null);
    setAnalysisResult(null);
    setMapping({});
    setImportResult(null);
  }, []);

  return {
    step,
    setStep,
    fileBase64,
    setFileBase64,
    analysisResult,
    setAnalysisResult,
    mapping,
    setMapping,
    importResult,
    setImportResult,
    goToMapping,
    goToImporting,
    goToSuccess,
    goBack,
    reset,
  };
};
